import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Clock, Calendar, Sparkles } from "lucide-react";
import { Card } from "../components/ui/card";
import { POSTS, CATEGORIES } from "../data/blogPosts";

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const Hero = () => (
    <section className="relative overflow-hidden border-b border-brand-border" aria-labelledby="resources-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-white to-[#F1F7F8]" aria-hidden="true" />
        <div className="absolute -top-32 right-1/3 w-[420px] h-[420px] rounded-full bg-[#127A75]/10 blur-3xl -z-10" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Resources</p>
            <h1 id="resources-hero" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight max-w-4xl">
                Resources &amp; Insights
            </h1>
            <p className="mt-5 text-lg text-brand-body max-w-3xl">
                Deep dives on accessibility, compliance, and inclusive design from 12+ years in the field.
            </p>
        </div>
    </section>
);

/* Decorative cover for posts (no stock photos) */
const PostCover = ({ post, large }) => {
    const seed = (post.slug || "").length;
    return (
        <div
            className={`relative w-full ${large ? "aspect-[16/8]" : "aspect-[16/9]"} overflow-hidden rounded-md bg-gradient-to-br from-[#0A2540] to-[#127A75]`}
            aria-hidden="true"
        >
            <svg viewBox="0 0 400 200" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-40">
                <circle cx={60 + seed * 7} cy="60" r="40" fill="#1A8B82" />
                <circle cx="320" cy="140" r="60" fill="#FFFFFF" opacity="0.2" />
                <rect x={140 + seed} y="80" width="80" height="80" rx="12" fill="#FFFFFF" opacity="0.15" />
            </svg>
            <span className="absolute bottom-3 left-4 text-xs font-bold uppercase tracking-wider text-white/80">
                {post.category}
            </span>
        </div>
    );
};

const PostCard = ({ post, large = false, idx = 0 }) => {
    const Inner = (
        <Card className={`h-full ${large ? "p-7" : "p-6"} bg-white border-brand-border hover:shadow-cardHover transition-shadow`}>
            <PostCover post={post} large={large} />
            <div className="mt-5">
                <div className="flex items-center gap-3 text-xs font-semibold text-brand-muted">
                    <span className="text-brand-teal uppercase tracking-wider">{post.category}</span>
                    <span aria-hidden="true">·</span>
                    <span className="flex items-center gap-1">
                        <Clock size={12} aria-hidden="true" /> {post.readTime}
                    </span>
                </div>
                <h3 className={`mt-3 ${large ? "text-2xl sm:text-3xl" : "text-lg"} font-bold text-brand-text leading-tight`}>
                    {post.title}
                </h3>
                <p className={`mt-3 text-sm text-brand-body leading-relaxed ${large ? "" : "line-clamp-3"}`}>
                    {post.excerpt}
                </p>
                <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1 text-xs text-brand-muted">
                        <Calendar size={12} aria-hidden="true" />
                        {formatDate(post.date)}
                    </span>
                    {post.published ? (
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-teal">
                            Read article <ArrowRight size={14} aria-hidden="true" />
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-muted bg-brand-soft border border-brand-border rounded-full px-2.5 py-0.5">
                            <Sparkles size={12} aria-hidden="true" /> Coming soon
                        </span>
                    )}
                </div>
            </div>
        </Card>
    );

    if (post.published) {
        return (
            <Link
                to={`/resources/${post.slug}`}
                className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tealBright focus-visible:ring-offset-2 rounded-lg"
                aria-label={`Read: ${post.title}`}
                data-testid={`post-card-${post.slug}`}
            >
                {Inner}
            </Link>
        );
    }
    return <div data-testid={`post-card-${post.slug}`}>{Inner}</div>;
};

const FilterBar = ({ active, setActive, q, setQ }) => (
    <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-10">
        <ul role="tablist" aria-label="Filter by category" className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
                const isActive = active === c;
                return (
                    <li key={c}>
                        <button
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActive(c)}
                            className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors min-h-[40px] ${
                                isActive
                                    ? "bg-brand-navy text-white border-brand-navy"
                                    : "bg-white text-brand-text border-brand-border hover:border-brand-teal hover:text-brand-teal"
                            }`}
                            data-testid={`filter-${c.toLowerCase()}`}
                        >
                            {c}
                        </button>
                    </li>
                );
            })}
        </ul>
        <div className="relative lg:w-72">
            <label htmlFor="post-search" className="sr-only">Search articles</label>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" aria-hidden="true" />
            <input
                id="post-search"
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles…"
                className="w-full min-h-[44px] pl-9 pr-4 rounded-md border border-brand-border bg-white text-sm text-brand-text placeholder:text-brand-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tealBright focus-visible:border-brand-tealBright"
                data-testid="post-search-input"
            />
        </div>
    </div>
);

export default function Resources() {
    useEffect(() => { document.title = "Resources — AccessNova"; }, []);
    const [active, setActive] = useState("All");
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        return POSTS.filter((p) => {
            const cat = active === "All" || p.category === active;
            const text = (p.title + " " + (p.excerpt || "")).toLowerCase();
            const search = !q || text.includes(q.toLowerCase());
            return cat && search;
        });
    }, [active, q]);

    const featured = filtered.find((p) => p.featured && p.published);
    const rest = filtered.filter((p) => p.slug !== featured?.slug);

    return (
        <>
            <Hero />
            <section aria-labelledby="resources-grid" className="py-16 lg:py-20">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <h2 id="resources-grid" className="sr-only">All articles</h2>
                    <FilterBar active={active} setActive={setActive} q={q} setQ={setQ} />

                    {featured && (
                        <div className="mb-12">
                            <PostCard post={featured} large />
                        </div>
                    )}

                    {rest.length === 0 && !featured ? (
                        <p className="text-center text-brand-muted py-16" data-testid="no-results">
                            No articles match your filters yet.
                        </p>
                    ) : (
                        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {rest.map((p) => (
                                <li key={p.slug}>
                                    <PostCard post={p} />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>
        </>
    );
}
